import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Apple,
  CalendarClock,
  CheckCircle2,
  Clock,
  CreditCard,
  PauseCircle,
  Play,
  Search,
  ShieldCheck,
  ShieldX,
  Sparkles,
  Store,
  TestTube,
  XCircle,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { isEffectivePro, isStalePro } from "@/lib/subscription";
import { DataTable, type Column } from "@/components/data/DataTable";
import { KpiCard } from "@/components/data/KpiCard";
import { RelTime } from "@/components/data/RelTime";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  paginatedItems,
  useSubscriptions,
  useSubscriptionStats,
  useSubscriptionEvents,
} from "@/lib/queries";
import { fmtDate, truncate } from "@/lib/format";
import type { SubscriptionEventRow, SubscriptionRow } from "@/types";

const PAGE = 50;

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "grace", label: "Grace period" },
  { value: "billing_issue", label: "Billing issue" },
  { value: "churned", label: "Churned" },
];

const ENV_OPTIONS = [
  { value: "all", label: "All environments" },
  { value: "production", label: "Production" },
  { value: "sandbox", label: "Sandbox" },
];

const EVENT_TYPE_OPTIONS = [
  { value: "all", label: "All events" },
  { value: "INITIAL_PURCHASE", label: "Initial purchase" },
  { value: "RENEWAL", label: "Renewal" },
  { value: "CANCELLATION", label: "Cancellation" },
  { value: "UNCANCELLATION", label: "Uncancellation" },
  { value: "EXPIRATION", label: "Expiration" },
  { value: "BILLING_ISSUE", label: "Billing issue" },
  { value: "PRODUCT_CHANGE", label: "Product change" },
  { value: "TRANSFER", label: "Transfer" },
  { value: "REST_SYNC", label: "REST sync" },
  { value: "TEST", label: "Test" },
];

function storeBadge(store: string | null) {
  if (!store) return <span className="text-xs text-muted-foreground">—</span>;
  const key = store.toUpperCase();
  if (key === "APP_STORE" || key === "MAC_APP_STORE") {
    return <Badge variant="info"><Apple className="mr-1 h-3 w-3" />App Store</Badge>;
  }
  if (key === "PLAY_STORE") {
    return <Badge variant="info"><Play className="mr-1 h-3 w-3" />Play Store</Badge>;
  }
  if (key === "STRIPE" || key === "RC_BILLING") {
    return <Badge variant="info"><CreditCard className="mr-1 h-3 w-3" />{store}</Badge>;
  }
  if (key === "PROMOTIONAL") {
    return <Badge variant="default">Promo</Badge>;
  }
  return <Badge variant="default"><Store className="mr-1 h-3 w-3" />{store}</Badge>;
}

function statusBadge(row: SubscriptionRow) {
  if (isStalePro(row)) {
    return <Badge variant="error"><ShieldX className="mr-1 h-3 w-3" />Expired (stale)</Badge>;
  }
  const pro = isEffectivePro(row);
  if (pro && row.is_in_grace_period) {
    return <Badge variant="warn"><PauseCircle className="mr-1 h-3 w-3" />Grace</Badge>;
  }
  if (pro && row.billing_issues_detected) {
    return <Badge variant="warn"><AlertTriangle className="mr-1 h-3 w-3" />Billing issue</Badge>;
  }
  if (pro && row.unsubscribe_detected) {
    return <Badge variant="warn"><Clock className="mr-1 h-3 w-3" />Will not renew</Badge>;
  }
  if (pro) {
    return <Badge variant="success"><ShieldCheck className="mr-1 h-3 w-3" />Active</Badge>;
  }
  return <Badge variant="error"><ShieldX className="mr-1 h-3 w-3" />Inactive</Badge>;
}

function envBadge(row: SubscriptionRow) {
  if (row.is_sandbox) {
    return <Badge variant="warn" className="font-mono text-[10px]"><TestTube className="mr-1 h-3 w-3" />SANDBOX</Badge>;
  }
  return <Badge variant="default" className="font-mono text-[10px]">{row.environment ?? "—"}</Badge>;
}

function fmtExpiry(ms: number | null | undefined): string {
  if (ms == null) return "—";
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return "—";
  const now = Date.now();
  const diff = ms - now;
  const days = Math.round(diff / 86400000);
  const dateStr = fmtDate(date.toISOString());
  if (Math.abs(days) > 14) return dateStr;
  if (days === 0) return `${dateStr} (today)`;
  if (days > 0) return `${dateStr} (in ${days}d)`;
  return `${dateStr} (${-days}d ago)`;
}

export function SubscriptionsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "subscribers";

  const stats = useSubscriptionStats();

  return (
    <>
      <PageHeader
        title="Subscriptions"
        description="RevenueCat entitlements + webhook event feed. Use this to debug who's Pro, why, and what RC just told us."
      />

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard
          icon={<ShieldCheck className="h-4 w-4" />}
          label="Active Pro"
          value={stats.isLoading ? "…" : stats.isError ? "—" : stats.data?.active_pro ?? 0}
          accent="success"
        />
        <KpiCard
          icon={<Sparkles className="h-4 w-4" />}
          label="Production"
          value={stats.isLoading ? "…" : stats.isError ? "—" : stats.data?.production_pro ?? 0}
          accent="info"
        />
        <KpiCard
          icon={<TestTube className="h-4 w-4" />}
          label="Sandbox"
          value={stats.isLoading ? "…" : stats.isError ? "—" : stats.data?.sandbox_pro ?? 0}
          accent="warn"
        />
        <KpiCard
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Billing issues"
          value={stats.isLoading ? "…" : stats.isError ? "—" : stats.data?.billing_issue ?? 0}
          accent={stats.data && stats.data.billing_issue > 0 ? "danger" : "default"}
        />
        <KpiCard
          icon={<CalendarClock className="h-4 w-4" />}
          label="Events 24h"
          value={stats.isLoading ? "…" : stats.isError ? "—" : stats.data?.events_24h ?? 0}
        >
          <div className="mt-1 text-xs text-muted-foreground">
            {stats.isError ? (
              <button type="button" className="underline hover:text-foreground" onClick={() => stats.refetch()}>
                Couldn't load stats · Retry
              </button>
            ) : stats.data?.last_event_at_ms ? (
              <>last: <RelTime value={stats.data.last_event_at_ms} /></>
            ) : stats.data ? (
              "no events yet"
            ) : null}
          </div>
        </KpiCard>
      </div>

      {stats.data && stats.data.unmatched_app_user_ids > 0 ? (
        <Card className="mb-4 border-amber-500/30 bg-amber-500/5">
          <CardContent className="flex items-center gap-3 py-3 text-sm text-amber-200">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              <span className="font-semibold">{stats.data.unmatched_app_user_ids}</span> RevenueCat
              {" "}app_user_id{stats.data.unmatched_app_user_ids === 1 ? "" : "s"} have events but no linked BillyPocket account yet. These will auto-link the moment the user signs up and iOS calls{" "}
              <code className="font-mono">Purchases.logIn(user.id)</code>.
            </span>
          </CardContent>
        </Card>
      ) : null}

      <Tabs
        value={tab}
        onValueChange={(value) => {
          const next = new URLSearchParams(params);
          next.set("tab", value);
          setParams(next, { replace: true });
        }}
      >
        <TabsList>
          <TabsTrigger value="subscribers">Subscribers</TabsTrigger>
          <TabsTrigger value="events">Event feed</TabsTrigger>
        </TabsList>

        <TabsContent value="subscribers" className="mt-4">
          <SubscribersTab />
        </TabsContent>

        <TabsContent value="events" className="mt-4">
          <EventsTab />
        </TabsContent>
      </Tabs>
    </>
  );
}

function SubscribersTab() {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [environment, setEnvironment] = useState("all");

  const params = useMemo(
    () => ({ limit: PAGE, offset: page * PAGE, status, environment, search: search.trim() || undefined }),
    [page, status, environment, search]
  );

  const q = useSubscriptions(params);
  const rows = paginatedItems<SubscriptionRow>(q.data);
  const total = q.data?.total ?? rows.length;

  const cols: Column<SubscriptionRow>[] = [
    {
      key: "user",
      header: "User",
      cell: (r) => (
        <Link
          to={`/subscriptions/${r.user_id}`}
          className="flex flex-col gap-0.5 hover:underline"
        >
          <span className="text-sm font-medium">{r.name ?? "—"}</span>
          <span className="text-xs text-muted-foreground">{r.email ?? r.user_id.slice(0, 8)}</span>
        </Link>
      ),
    },
    { key: "status", header: "Status", cell: statusBadge, width: 140 },
    { key: "env", header: "Env", cell: envBadge, width: 110 },
    { key: "store", header: "Store", cell: (r) => storeBadge(r.store), width: 140 },
    {
      key: "product",
      header: "Product",
      cell: (r) => (
        <span className="font-mono text-xs text-muted-foreground">
          {r.product_id ? truncate(r.product_id, 32) : "—"}
        </span>
      ),
    },
    {
      key: "expires",
      header: "Expires",
      cell: (r) => <span className="text-xs">{fmtExpiry(r.expires_at_ms)}</span>,
      width: 170,
    },
    {
      key: "renew",
      header: "Renew",
      cell: (r) =>
        r.will_renew === true ? (
          <Badge variant="success" className="text-[10px]"><CheckCircle2 className="mr-1 h-3 w-3" />Yes</Badge>
        ) : r.will_renew === false ? (
          <Badge variant="warn" className="text-[10px]"><XCircle className="mr-1 h-3 w-3" />No</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
      width: 90,
    },
    {
      key: "latest",
      header: "Latest event",
      cell: (r) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-mono text-[11px] text-muted-foreground">{r.latest_event_type ?? "—"}</span>
          {r.latest_event_at_ms ? (
            <span className="text-[10px] text-muted-foreground">
              <RelTime value={r.latest_event_at_ms} />
            </span>
          ) : null}
        </div>
      ),
      width: 170,
    },
  ];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Email, name, user id, RC app user id"
            className="h-8 w-64 pl-8 text-sm"
          />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0); }}>
          <SelectTrigger className="h-8 w-[160px] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={environment} onValueChange={(v) => { setEnvironment(v); setPage(0); }}>
          <SelectTrigger className="h-8 w-[160px] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ENV_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={cols}
        rows={rows}
        loading={q.isLoading}
        error={q.isError ? q.error : undefined}
        onRetry={() => q.refetch()}
        rowKey={(r) => r.user_id}
        page={page}
        onPageChange={setPage}
        pageSize={PAGE}
        totalRows={total}
        emptyTitle="No subscriptions yet"
        emptyDescription="When iOS users buy via RevenueCat, their state appears here."
      />
    </>
  );
}

function EventsTab() {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [environment, setEnvironment] = useState("all");
  const [processed, setProcessed] = useState("all");

  const params = useMemo(
    () => ({
      limit: PAGE,
      offset: page * PAGE,
      type,
      environment,
      processed,
      search: search.trim() || undefined,
    }),
    [page, type, environment, processed, search]
  );

  const q = useSubscriptionEvents(params);
  const rows = paginatedItems<SubscriptionEventRow>(q.data);
  const total = q.data?.total ?? rows.length;

  const cols: Column<SubscriptionEventRow>[] = [
    {
      key: "time",
      header: "Time",
      cell: (r) => (
        <span className="text-xs text-muted-foreground">
          <RelTime value={r.event_at_ms ?? r.created_at} />
        </span>
      ),
      width: 130,
    },
    {
      key: "type",
      header: "Event",
      cell: (r) => (
        <Badge variant={r.event_type === "TEST" ? "default" : r.event_type.includes("ISSUE") || r.event_type === "EXPIRATION" ? "warn" : "info"} className="font-mono text-[10px]">
          {r.event_type}
        </Badge>
      ),
      width: 180,
    },
    {
      key: "trigger",
      header: "Trigger",
      cell: (r) => (
        <span className="font-mono text-[10px] text-muted-foreground">{r.trigger}</span>
      ),
      width: 90,
    },
    {
      key: "user",
      header: "User",
      cell: (r) =>
        r.user_id ? (
          <Link to={`/subscriptions/${r.user_id}`} className="font-mono text-[11px] text-sky-300 hover:underline">
            {r.user_id.slice(0, 12)}
          </Link>
        ) : (
          <span className="font-mono text-[11px] text-amber-300/80" title="No BillyPocket account linked to this RC app_user_id yet.">
            (unmatched)
          </span>
        ),
      width: 140,
    },
    {
      key: "rc_user",
      header: "RC user",
      cell: (r) => (
        <span className="font-mono text-[10px] text-muted-foreground">{truncate(r.rc_app_user_id, 16)}</span>
      ),
      width: 170,
    },
    {
      key: "env",
      header: "Env",
      cell: (r) => (
        <Badge variant={r.environment === "SANDBOX" ? "warn" : "default"} className="font-mono text-[10px]">
          {r.environment ?? "—"}
        </Badge>
      ),
      width: 110,
    },
    {
      key: "processed",
      header: "Processed",
      cell: (r) =>
        r.processed ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
        ) : r.error ? (
          <XCircle className="h-4 w-4 text-rose-400" />
        ) : (
          <Clock className="h-4 w-4 text-muted-foreground" />
        ),
      width: 90,
    },
    {
      key: "event_id",
      header: "Event id",
      cell: (r) => (
        <span className="font-mono text-[10px] text-muted-foreground">
          {r.event_id ? r.event_id.slice(0, 12) : "—"}
        </span>
      ),
      width: 140,
    },
  ];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Event id, user id, RC app user id"
            className="h-8 w-72 pl-8 text-sm"
          />
        </div>
        <Select value={type} onValueChange={(v) => { setType(v); setPage(0); }}>
          <SelectTrigger className="h-8 w-[160px] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {EVENT_TYPE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={environment} onValueChange={(v) => { setEnvironment(v); setPage(0); }}>
          <SelectTrigger className="h-8 w-[160px] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ENV_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={processed} onValueChange={(v) => { setProcessed(v); setPage(0); }}>
          <SelectTrigger className="h-8 w-[150px] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All states</SelectItem>
            <SelectItem value="true">Processed</SelectItem>
            <SelectItem value="false">Unprocessed</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => q.refetch()}>Refresh</Button>
      </div>

      <DataTable
        columns={cols}
        rows={rows}
        loading={q.isLoading}
        error={q.isError ? q.error : undefined}
        onRetry={() => q.refetch()}
        rowKey={(r) => r.id}
        page={page}
        onPageChange={setPage}
        pageSize={PAGE}
        totalRows={total}
        emptyTitle="No webhook events"
        emptyDescription="Hit “Send test event” in the RevenueCat dashboard to verify the integration."
      />
    </>
  );
}
